import { Box, Paper, Text } from "@mantine/core"
import type { Icon as PhosphorIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { HelpIcon } from "@dehub/theme"
import classes from "../real.module.css"

export interface KpiTileProps {
  /** Drawn dark on a gray tile, the same for every card: colour is kept for status, not decoration. */
  icon: PhosphorIcon
  label: ReactNode
  /** @dehub/help `field` key: the «?» after the label. */
  helpKey?: string
  value: ReactNode
  unit?: ReactNode
  /** The value is a name, not a figure (e.g. the top-margin product): smaller, wraps. */
  textValue?: boolean
  /** Line(s) under the figure: change vs last month (and last year), or a related figure.
   *  Omitted or empty → «—», so every tile in a row reads the same. */
  foot?: ReactNode | ReactNode[]
}

// The one dashboard KPI card: small gray icon tile + sentence-case label + «?»,
// big figure with a muted unit, one neutral line under it. Used by the KPI row
// and the returns overview so the two rows can never drift apart.
export function KpiTile({ icon: Icon, label, helpKey, value, unit, textValue, foot }: KpiTileProps) {
  const lines = (Array.isArray(foot) ? foot : [foot]).filter((l) => l !== undefined && l !== null && l !== false)
  return (
    <Paper className={classes.statCard}>
      <Box className={classes.statHead}>
        <Box component="span" className={classes.statIcon} aria-hidden="true">
          <Icon size={16} weight="bold" />
        </Box>
        <Text className={classes.statLbl}>
          {label}
          {helpKey && <HelpIcon helpKey={helpKey} />}
        </Text>
      </Box>
      <Text className={`${classes.statVal} ${textValue ? classes.statValText : ""}`}>
        {value}
        {unit ? <Text component="span" className={classes.statUnit}>{unit}</Text> : null}
      </Text>
      <Box className={classes.statFoot}>
        {lines.length === 0 ? (
          <Box className={classes.statChange}>—</Box>
        ) : (
          lines.map((line, i) => <Box key={i} className={classes.statChange}>{line}</Box>)
        )}
      </Box>
    </Paper>
  )
}
